import {
    Box,
    Grid,
    Badge,
    Group,
    Text,
    Loader
} from "@mantine/core";
import React, { useState } from "react";
import InputField, { InputFieldTypes } from "../input_field";
import VoiceSelectorModal from "../voice_selector_modal";
import { useLanguage } from "../../contexts/language.context";
import { normalizeAgentLanguages, useAgentVariables, useApp } from "../../contexts/app.context";
import { Voice } from "../../types/voice";
import { useVoiceSelector } from "../../hooks/useVoiceSelector";

export default function Settings({ data, voices, setData, setVoice, knowledges = [] }: {
    data: any, voices: any[], setData: any, setVoice: any, knowledges: any[]
}) {
    const { str } = useLanguage();
    const { agentRoles, agentLanguages } = useApp();
    const companyVariables = useAgentVariables();
    const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);
    const voiceSelector = useVoiceSelector();

    // Get the current selected voice
    const currentVoice = voiceSelector.findVoiceByRetellId(data?.data?.retell?.agent?.voice_id);

    // Handle voice selection from modal
    const handleVoiceSelect = (voice: Voice) => {
        setData((na: any) => ({
            ...na,
            data: {
                ...na.data,
                retell: {
                    ...na?.data?.retell,
                    agent: {
                        ...na?.data?.retell?.agent,
                        voice_id: voice.retell_voice_id
                    }
                }
            }
        }));
        setIsVoiceModalOpen(false);
    };

    return <Box p="md">
        <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
                <InputField
                    name="title"
                    title={str("AGENTS.AGENT_NAME")}
                    value={data?.title}
                    onChange={({ title }) => setData(na => ({ ...na, title }))}
                />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
                <Box>
                    <InputField
                        name="voice_id"
                        title={str("AGENTS.SELECT_VOICE")}
                        onClick={() => setIsVoiceModalOpen(true)}
                        value={currentVoice?.name || "Select a voice"}
                        readOnly
                        leftSection={voiceSelector.isLoading && <Loader size="xs" color="white" />}
                    />
                    {currentVoice && (
                        <Group gap="xs" mt="xs">
                            <Badge size="xs" variant="outline" color="indigo">
                                {currentVoice.provider}
                            </Badge>
                            {currentVoice.is_default && (
                                <Badge size="xs" variant="dot" color="blue">
                                    Default
                                </Badge>
                            )}
                            {currentVoice.is_workspace_voice && (
                                <Badge size="xs" variant="dot" color="grape">
                                    Workspace
                                </Badge>
                            )}
                        </Group>
                    )}
                </Box>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
                <InputField
                    name="language"
                    title={str("LANGUAGE")}
                    value={normalizeAgentLanguages(data?.data?.retell?.agent?.language, ["en-US"])}
                    fieldType={InputFieldTypes.SELECT}
                    multiple
                    clearable={false}
                    onChange={({ language }) => setData(na => ({
                        ...na, data: {
                            ...na.data, retell: {
                                ...na?.data?.retell, agent: { ...na?.data?.retell?.agent, language }
                            }
                        }
                    }))}
                    options={agentLanguages}
                />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
                <InputField
                    name="role"
                    title={str("AGENTS.ROLE")}
                    placeholder={str("AGENTS.ROLE_PLACEHOLDER")}
                    value={data?.data?.role}
                    onChange={({ role }) => setData(na => ({ ...na, data: { ...na.data, role } }))}
                />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
                <InputField
                    name="knowledges"
                    title="Knowledges Bases"
                    mb="md"
                    multiple
                    fieldType={InputFieldTypes.SELECT}
                    value={(data?.knowledges ?? []).map(k => k.id || k)}
                    options={knowledges.map(k => ({ label: k.title, value: k.id }))}
                    onChange={({ knowledges }) => setData(na => ({ ...na, knowledges }))}
                />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
                <InputField
                    name="ambient_sound"
                    title={str("AGENTS.AMBIENT_SOUND")}
                    value={data?.data?.retell?.agent?.ambient_sound}
                    fieldType={InputFieldTypes.SELECT}
                    clearable={true}
                    searchable={false}
                    onChange={({ ambient_sound }) => setData(na => ({
                        ...na, data: {
                            ...na.data, retell: {
                                ...na?.data?.retell, agent: { ...na?.data?.retell?.agent, ambient_sound }
                            }
                        }
                    }))}
                    options={[
                        { label: str("COFFEE_SHOP"), value: "coffee-shop" },
                        { label: str("CONVENTION_HALL"), value: "convention-hall" },
                        { label: str("SUMMER_OUTDOOR"), value: "summer-outdoor" },
                        { label: str("MOUNTAIN_OUTDOOR"), value: "mountain-outdoor" },
                        { label: str("STATIC_NOISE"), value: "static-noise" },
                    ]}
                />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
                <InputField
                    name="company_info"
                    title={str("AGENTS.COMPANY_INFO_TITLE")}
                    description={str("AGENTS.COMPANY_INFO_DESCRIPTION")}
                    placeholder={str("AGENTS.COMPANY_INFO_PLACEHOLDER")}
                    options={companyVariables}
                    value={data?.data?.company_info}
                    fieldType={InputFieldTypes.TEXTAREA}
                    minRows={8}
                    maxRows={8}
                    maxLength={1000}
                    onChange={({ company_info }) => setData(na => ({ ...na, data: { ...na.data, company_info } }))}
                />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
                <InputField
                    name="objective"
                    title={str("AGENTS.OBJECTIVE_TITLE")}
                    description={str("AGENTS.OBJECTIVE_DESCRIPTION")}
                    options={companyVariables}
                    maxLength={1000}
                    placeholder={str("AGENTS.OBJECTIVE_PLACEHOLDER")}
                    value={data?.data?.objective}
                    fieldType={InputFieldTypes.TEXTAREA}
                    minRows={8}
                    maxRows={8}
                    onChange={({ objective }) => setData(na => ({ ...na, data: { ...na.data, objective } }))}
                />
            </Grid.Col>
        </Grid>

        {/* Voice Selector Modal */}
        <VoiceSelectorModal
            opened={isVoiceModalOpen}
            onClose={() => setIsVoiceModalOpen(false)}
            onSelect={handleVoiceSelect}
            currentVoiceId={data?.data?.retell?.agent?.voice_id}
        />
    </Box>
}